import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Lock, Mail, AlertCircle } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  
  // Check for recovery parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isRecovery = params.get('recovery') === '1';
    const expired = params.get('expired') === 'true';
    const invalid = params.get('invalid') === 'true';
    
    if (isRecovery || expired || invalid) {
      setIsRecoveryMode(true);
      
      if (expired) {
        setError("Votre session a expiré. Veuillez vous reconnecter.");
      } else if (invalid) {
        setError("Session invalide. Veuillez vous reconnecter.");
      }
    }
  }, [location]);
  
  // For deployed sites, check if we have a redirect path
  useEffect(() => {
    if (import.meta.env.PROD && user) {
      try {
        const redirectPath = sessionStorage.getItem('redirectPath');
        if (redirectPath) {
          sessionStorage.removeItem('redirectPath');
          navigate(redirectPath);
        }
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    
    // For deployed sites, track login attempts
    if (import.meta.env.PROD) {
      try {
        localStorage.setItem('lastLoginAttempt', new Date().toISOString());
      } catch (e) {
        // Ignore storage errors
      }
    }

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.error || 'Une erreur est survenue lors de la connexion');
    } else {
      // Determine where to redirect after login
      const redirectPath = sessionStorage.getItem('redirectPath') || '/dashboard';
      
      // Clear any stored paths
      try {
        sessionStorage.removeItem('redirectPath');
      } catch (e) {
        // Ignore storage errors
      }
      
      // Navigate to the appropriate page
      navigate(redirectPath);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 animate-fade-in">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-primary-600">FormaNote by FormaVerse</h1>
          <h2 className="mt-6 text-2xl font-bold text-gray-900">Connexion</h2>
          <p className="mt-2 text-sm text-gray-600">
            Accédez à votre compte pour gérer les notes
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-error-50 text-error-700 p-3 rounded-md flex items-start">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          
          {isRecoveryMode && (
            <div className="bg-amber-50 text-amber-700 p-3 rounded-md">
              <p>Tentative de connexion après récupération. Si vous rencontrez des problèmes persistants, essayez de vider le cache de votre navigateur.</p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field pl-10"
                  placeholder="votre@email.com"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Mot de passe
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pl-10"
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                Se souvenir de moi
              </label>
            </div>

            <div className="text-sm">
              <a href="#" className="font-medium text-primary-600 hover:text-primary-500">
                Mot de passe oublié?
              </a>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn btn-primary flex justify-center items-center"
            >
              {isLoading ? (
                <>
                  <span className="animate-spin mr-2">
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                  Connexion...
                </>
              ) : (
                'Se connecter'
              )}
            </button>
          </div>
        </form>
        
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600">
            Pas encore de compte ? Contactez votre administrateur FormaVerse.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;